import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {UserBearerToken} from '../../shared/responses/bearer-token';
import {Store} from '@ngrx/store';
import {UserLoggedIn} from '../actions/session.actions';
import {AppState} from '../../app/app.state';
import {Router} from '@angular/router';
import {API_BASE_URL} from './env.service';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {User, USER_ROLE_ADMIN, USER_ROLE_ROOT_ADMIN, UserId} from '../../shared/models/user';
import {TotpSetup} from '../../shared/models/totp';
import {ShadeToolsService} from './shade-tools.service';
import {PermissionsService} from './permissions.service';

const LS_USER_ID = 'v2-userId';
const LS_USER_JSON = 'v2-user';
const LS_BEARER_TOKEN = 'v2-bearerToken';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  user$: BehaviorSubject<User> = new BehaviorSubject<User>(null);

  emulating: boolean = false;
  userId: UserId;
  user: User;
  private bearerToken: string;
  private bearerTokenCheckInterval: ReturnType<typeof setInterval>;


  constructor(
    public http: HttpClient,
    private store: Store<AppState>,
    private router: Router,
    private shadeTools: ShadeToolsService,
    private permissionsService: PermissionsService,
  ) {
    this.userId = localStorage.getItem(LS_USER_ID);
    this.bearerToken = localStorage.getItem(LS_BEARER_TOKEN);

    this.setUser(JSON.parse(localStorage.getItem(LS_USER_JSON)));

    if (this.bearerToken) {
      this.dispatchState();
    }

    this.startCheckingBearerTokenChanged();
  }


  setUser(user: User) {
    // Ensure user.ID is always a string (fix type inconsistency from backend/localStorage)
    if (user && user.ID !== undefined && user.ID !== null) {
      user.ID = String(user.ID) as UserId;
    }
    this.user = user;
    this.user$.next(user);
  }

  getBearerToken(): string {
    return this.bearerToken;
  }

  /**
   * Returns the bearer token without the user ID prefix
   * Format: userID.actualToken -> actualToken
   */
  getCleanBearerToken(): string {
    if (!this.bearerToken) {
      return null;
    }
    const tokenParts = this.bearerToken.split('.');
    return tokenParts.length > 1 ? tokenParts.slice(1).join('.') : this.bearerToken;
  }


  /**
   * If the bearer token changed, that most likely means the user logged out
   * or logged in as the same or different user in another tab.
   *
   * @private
   */
  private startCheckingBearerTokenChanged() {
    clearInterval(this.bearerTokenCheckInterval);

    this.bearerTokenCheckInterval = setInterval(() => {
      if (this.bearerToken && this.bearerToken != localStorage.getItem(LS_BEARER_TOKEN)) {
        this.shadeTools.startDialog(
          `Session Changed`,
          `This browser's session data has changed, likely because you logged out in a different tab.\n\nReload the platform to continue.`,
          [{
            text: 'Reload',
            callback: () => {
              window.location.reload();
            },
          }],
          false,
          false,
          false,
          false,
        );
      }
    }, 10000);
  }


  /**
   * Logs out and optionally redirects user to login page.
   *
   * @param redirectToLogin - Set to false for processes that don't need
   *    redirect to login page, such as password reset.
   */
  logOut(redirectToLogin: boolean = true): void {
    const bearerToken = this.bearerToken;

    this.permissionsService.clearCache();

    localStorage.clear();

    if (!redirectToLogin) {
      // Only clear this if we're not redirecting because otherwise
      // certain UI elements will react to the newly nulled bearer
      // token in weird ways on a traditional logout
      this.bearerToken = null;
    }

    this.http.delete(`${API_BASE_URL}/sessions/${bearerToken}`).subscribe(
      () => {
        this.afterApiLogOut(redirectToLogin);
      },
      () => {
        this.afterApiLogOut(redirectToLogin);
      },
    );
  }


  private afterApiLogOut(redirectToLogin: boolean = true): void {
    if (redirectToLogin) {
      // If the following line is ever removed, then native-ios.service.ts needs
      // to be changed because it will only deregister a device on the load of
      // the header component
      setTimeout(() => {
        window.location.assign('/login');
      }, 0);
    }
  }


  get totpLocked(): boolean {
    return !!localStorage.getItem('v2-totpLocked');
  }


  set totpLocked(value) {
    if (value) {
      localStorage.setItem('v2-totpLocked', 'true');
    } else {
      localStorage.removeItem('v2-totpLocked');
    }
  }


  /**
   * Answers the question: "Is the user currently assumed to be authenticated?"
   *
   * Keep in mind the user may be considered authenticated while still needing
   * to complete a 2FA challenge.
   */
  hasAuthenticated(): boolean {
    return !!this.bearerToken && !this.totpLocked;
  }


  /**
   *
   */
  refreshUserInfo$(): Observable<User> {
    return new Observable<User>(
      subscriber => {
        this.http.get<UserBearerToken>(`${API_BASE_URL}/sessions/${this.bearerToken}`).subscribe(
          (data) => {
            this.setUser(data.User);
            this.handleBearerTokenResponse(data);
            subscriber.next(data.User);
            subscriber.complete();
          },
          (error) => {
            subscriber.error(error);
          },
        );
      },
    );
  }


  /**
   *
   * @param username
   * @param password
   */
  authenticate$(username: string, password: string): Subject<UserBearerToken> {
    const result = new Subject<UserBearerToken>();

    this.http.post<UserBearerToken>(
      `${API_BASE_URL}/sessions`,
      JSON.stringify({
        Username: username,
        Password: password,
      }),
    ).subscribe({
      next: (data: UserBearerToken) => {
        if (data.User.TOTPEnabled) {
          this.totpLocked = true;
        } else {
          this.totpLocked = false;
        }

        this.handleBearerTokenResponse(data);

        result.next(data);
        result.complete();
      },
      error: (err) => {
        result.error(err);
      },
    });

    return result;
  }


  validateTotp$(code: string): Subject<boolean> {
    const result = new Subject<boolean>();

    this.http.delete(`${API_BASE_URL}/sessions/locks?Code=${code}`).subscribe({
      next: () => {
        this.totpLocked = false;
        this.user.TOTPEnabled = true;
        this.dispatchState();
        this.storeStateToLocalStorage();
        result.next(true);
        result.complete();
      },
      error: () => {
        result.error(null);
        result.complete();
      },
    });

    return result;
  }


  enableTotp$(): Subject<TotpSetup> {
    const result = new Subject<TotpSetup>();

    this.http.post<TotpSetup>(`${API_BASE_URL}/users/${this.user.ID}/totp`, null).subscribe(
      (data: TotpSetup) => {
        result.next(data);
        result.complete();
      },
      () => {
        result.error(null);
        result.complete();
      },
    );

    return result;
  }


  disableTotp$(): Subject<boolean> {
    const result = new Subject<boolean>();

    this.http.delete(`${API_BASE_URL}/users/${this.user.ID}/totp`).subscribe(
      () => {
        this.user.TOTPEnabled = false;
        this.dispatchState();
        this.storeStateToLocalStorage();
        result.next(true);
        result.complete();
      },
      () => {
        result.error(null);
        result.complete();
      },
    );

    return result;
  }


  /**
   *
   * @param data
   * @private
   */
  private handleBearerTokenResponse(data: UserBearerToken) {
    this.bearerToken = data.BearerToken;
    this.userId = data.BearerToken.split('.')[0];
    this.setUser(data.User);

    this.dispatchState();
    this.storeStateToLocalStorage();
  }


  private storeStateToLocalStorage() {
    localStorage.setItem(LS_USER_ID, '' + this.userId);
    localStorage.setItem(LS_USER_JSON, JSON.stringify(this.user));
    localStorage.setItem(LS_BEARER_TOKEN, this.bearerToken);
  }


  /**
   * This should be called from within the browser window that is going to run the
   * emulated session.
   *
   * To launch an emulation from an admin account (the step before runEmulation),
   * use launchEmulation instead.
   *
   * @param data
   */
  runEmulation(data: UserBearerToken) {
    localStorage.clear();
    this.emulating = true;
    this.handleBearerTokenResponse(data);
    this.router.navigate(['/login'], {replaceUrl: true});
    this.router.navigate(['/apps']);
  }


  /**
   *
   */
  private async dispatchState() {
    // TODO: add displayName
    const action = new UserLoggedIn(
      'TODO',
      this.userId,
      this.user.Username,
      this.bearerToken,
      this.totpLocked,
    );

    await this.store.dispatch(action);
  }


  /**
   *
   */
  isUserAdmin(): boolean {
    if (!this.user || !this.hasAuthenticated()) {
      return false;
    }

    return (this.user.Roles & USER_ROLE_ROOT_ADMIN) > 0
      || (this.user.Roles & USER_ROLE_ADMIN) > 0;
  }
}
